import type { WidgetConfig, WidgetItem, WidgetItemId } from '../domain/company_widgets';
import type { User, UserIndustry } from '../domain/user';
import { AdNetworks } from '../pages/company-overview/AdNetworks';
import { Ecommerce } from '../pages/company-overview/Ecommerce';
import { Engagement } from '../pages/company-overview/Engagement';
import { Firmographics } from '../pages/company-overview/Firmographics';
import { SalesforceActivity } from '../pages/company-overview/SalesforceActivity';
import { Signals } from '../pages/company-overview/Signals';
import { Technologies } from '../pages/company-overview/Technologies';
import { TrafficShare } from '../pages/company-overview/TrafficShare';
import { TrafficVisits } from '../pages/company-overview/TrafficVisits';

export const COMPANY_OVERVIEW_WIDGETS_CONFIG: WidgetConfig = {
  firmographics: { component: Firmographics, name: 'company.firmographics' },
  signals: { component: Signals, name: 'company.signals' },
  salesforceActivity: { component: SalesforceActivity, name: 'company.salesforce_activity.title' },
  trafficVisits: { component: TrafficVisits, name: 'company.traffic_visits' },
  trafficShare: { component: TrafficShare, name: 'company.traffic_share' },
  technologies: { component: Technologies, name: 'company.technologies.title' },
  engagement: { component: Engagement, name: 'company.engagement' },
  adNetworks: { component: AdNetworks, name: 'company.ad-networks' },
  ecommerce: { component: Ecommerce, name: 'company.ecommerce' },
};

const DEFAULT_COMPANY_OVERVIEW_WIDGETS: WidgetItemId[] = [
  'firmographics',
  'signals',
  'salesforceActivity',
  'trafficVisits',
  'engagement',
  'trafficShare',
  'ecommerce',
  'technologies',
];

const INDUSTRY_WIDGETS_MAP: { [key in UserIndustry]: WidgetItemId[] } = {
  'Ad tech & Ad network': [
    'firmographics',
    'signals',
    'salesforceActivity',
    'trafficVisits',
    'engagement',
    'trafficShare',
    'adNetworks',
    'technologies',
  ],
  'Consulting and market research': [
    'firmographics',
    'signals',
    'salesforceActivity',
    'trafficVisits',
    'engagement',
    'trafficShare',
    'adNetworks',
  ],
  'B2B software': [
    'firmographics',
    'signals',
    'salesforceActivity',
    'trafficVisits',
    'ecommerce',
    'technologies',
    'engagement',
  ],
  Payments: [
    'firmographics',
    'signals',
    'salesforceActivity',
    'ecommerce',
    'trafficVisits',
    'trafficShare',
    'technologies',
    'engagement',
  ],
  'Logistics and shipping': [
    'firmographics',
    'signals',
    'salesforceActivity',
    'ecommerce',
    'trafficVisits',
    'trafficShare',
    'technologies',
    'engagement',
  ],
  Agency: [
    'firmographics',
    'signals',
    'salesforceActivity',
    'trafficVisits',
    'engagement',
    'trafficShare',
    'adNetworks',
    'technologies',
  ],
  'Private equity and venture capital': [
    'firmographics',
    'signals',
    'salesforceActivity',
    'trafficVisits',
    'engagement',
    'trafficShare',
    'technologies',
  ],
  Investors: [
    'firmographics',
    'signals',
    'salesforceActivity',
    'trafficVisits',
    'engagement',
    'trafficShare',
    'technologies',
  ],
};

const createCompanyWidgetsService = () => {
  const isWidgetItemArray = (items: unknown): items is WidgetItem[] => {
    if (!Array.isArray(items)) {
      return false;
    }

    return items.every(
      (item) =>
        typeof item === 'object' &&
        item !== null &&
        'id' in item &&
        typeof (item as WidgetItem).id === 'string' &&
        'isChecked' in item &&
        typeof (item as WidgetItem).isChecked === 'boolean'
    );
  };

  const getDefaultCompanyWidgets = (user?: User): WidgetItemId[] => {
    if (!user || user.isTrialAccount || user.isAfterTrialAccount) {
      return DEFAULT_COMPANY_OVERVIEW_WIDGETS;
    }
    return user.accountIndustry && user.accountIndustry in INDUSTRY_WIDGETS_MAP
      ? INDUSTRY_WIDGETS_MAP[user.accountIndustry]
      : DEFAULT_COMPANY_OVERVIEW_WIDGETS;
  };

  const getSelectedWidgetsIds = (widgets: WidgetItem[]) =>
    widgets.filter(({ isChecked }) => isChecked).map(({ id }) => id);

  const getAllWidgetsIds = () => Object.keys(COMPANY_OVERVIEW_WIDGETS_CONFIG) as WidgetItemId[];

  const getWidgetName = (widgetId: WidgetItemId) => COMPANY_OVERVIEW_WIDGETS_CONFIG[widgetId].name;

  const widgetsAreEqual = (array1: WidgetItem[], array2: WidgetItem[]): boolean => {
    if (array1.length !== array2.length) {
      return false;
    }

    return array1.every(({ id, isChecked }, index) => id === array2[index].id && isChecked === array2[index].isChecked);
  };

  return {
    isWidgetItemArray,
    getDefaultCompanyWidgets,
    getAllWidgetsIds,
    getSelectedWidgetsIds,
    getWidgetName,
    widgetsAreEqual,
  };
};

const CompanyWidgetsService = createCompanyWidgetsService();

export default CompanyWidgetsService;
